import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { adminListBookings, adminUpdateBooking } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Phone, Mail, MessageSquare, CalendarClock, CheckCheck, X, Sparkles, Sun } from "lucide-react";

const ADMIN_EMAILS = new Set([
  "chahalsimmi725@gmail.com",
  "simmi@maplecharmbeautystudio.com",
]);

const STATUS_STYLES = {
  pending: "bg-blush/40 text-espresso",
  confirmed: "bg-blush text-espresso",
  completed: "bg-creamDeep text-mocha",
  cancelled: "bg-red-100 text-red-700 line-through",
};

export const Admin = () => {
  const { user, loading: authLoading, signIn } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const today = useMemo(() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }, []);

  const todaysBookings = useMemo(
    () =>
      bookings
        .filter((b) => b.date === today && b.status !== "cancelled")
        .sort((a, b) => (a.time || "").localeCompare(b.time || "")),
    [bookings, today]
  );

  useEffect(() => {
    if (authLoading) return;
    if (!user) return;
    if (!ADMIN_EMAILS.has(user.email?.toLowerCase())) return;
    adminListBookings()
      .then(setBookings)
      .catch(() => toast.error("Failed to load bookings"))
      .finally(() => setLoading(false));
  }, [user, authLoading]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-espresso" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="pt-20 px-6 text-center" data-testid="admin-signin-gate">
        <p className="script text-3xl text-gold">studio access</p>
        <h1 className="text-4xl font-serif text-espresso mt-1">Sign in as owner</h1>
        <p className="text-mocha mt-3 max-w-xs mx-auto text-sm">Only the studio owner can view bookings.</p>
        <button onClick={signIn} data-testid="admin-signin-btn" className="mt-8 px-6 py-3 rounded-full bg-espresso text-cream text-sm hover:shadow-glow">
          Sign in with Google
        </button>
      </div>
    );
  }

  if (!ADMIN_EMAILS.has(user.email?.toLowerCase())) {
    return (
      <div className="pt-20 px-6 text-center" data-testid="admin-forbidden">
        <p className="script text-3xl text-gold">oh no</p>
        <h1 className="text-4xl font-serif text-espresso mt-1">Not authorized</h1>
        <p className="text-mocha mt-3 max-w-xs mx-auto text-sm">Your account isn't a studio admin.</p>
        <button onClick={() => navigate("/")} className="mt-8 px-6 py-3 rounded-full bg-espresso text-cream text-sm">Back home</button>
      </div>
    );
  }

  const filtered = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);
  const upcoming = bookings.filter((b) => b.status === "pending" || b.status === "confirmed").length;

  const changeStatus = async (id, status) => {
    try {
      await adminUpdateBooking(id, status);
      setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Update failed");
    }
  };

  return (
    <div className="pt-10 px-6 pb-8" data-testid="admin-page">
      <p className="script text-2xl text-gold">studio owner</p>
      <h1 className="text-4xl font-serif text-espresso leading-tight">Bookings</h1>
      <p className="text-mocha text-sm mt-1">{bookings.length} total · {upcoming} upcoming</p>

      {/* Today's schedule at a glance */}
      {!loading && (
        <div
          className="mt-6 rounded-[1.75rem] p-5 bg-gradient-to-br from-blush/40 to-creamDeep shadow-soft"
          data-testid="today-panel"
        >
          <div className="flex items-center gap-2 mb-3">
            <Sun size={16} className="text-gold" />
            <p className="text-[11px] uppercase tracking-widest text-gold">Today · {new Date().toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</p>
          </div>
          {todaysBookings.length === 0 ? (
            <p className="text-mocha text-sm italic" data-testid="today-empty">No appointments today. Enjoy the calm ♡</p>
          ) : (
            <ul className="space-y-2" data-testid="today-list">
              {todaysBookings.map((b) => (
                <li
                  key={b.id}
                  className="flex items-center justify-between gap-3 bg-white/70 backdrop-blur rounded-2xl px-3 py-2"
                  data-testid={`today-item-${b.id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-espresso font-serif text-lg tabular-nums shrink-0">{b.time}</span>
                    <div className="min-w-0">
                      <p className="text-espresso text-sm truncate">{b.customer_name}</p>
                      <p className="text-mocha text-xs truncate">{b.service_name}</p>
                    </div>
                  </div>
                  <a
                    href={`tel:${b.customer_phone}`}
                    className="text-xs text-gold hover:text-espresso shrink-0 tabular-nums"
                    data-testid={`today-call-${b.id}`}
                  >
                    {b.customer_phone}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="mt-6 flex gap-2 overflow-x-auto -mx-6 px-6 pb-2">
        {["all", "pending", "confirmed", "completed", "cancelled"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`filter-${s}`}
            className={`shrink-0 px-4 py-2 rounded-full text-xs uppercase tracking-wider transition-all ${
              filter === s ? "bg-espresso text-cream" : "bg-white text-mocha border border-borderSoft"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mt-16 text-center"><Loader2 className="animate-spin text-espresso mx-auto" /></div>
      ) : filtered.length === 0 ? (
        <div className="mt-16 text-center text-mocha text-sm">
          <Sparkles className="mx-auto text-gold mb-2" size={22} />
          No bookings in this view.
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {filtered.map((b) => (
            <div key={b.id} className="bg-white rounded-[1.75rem] p-5 shadow-soft" data-testid={`booking-${b.id}`}>
              <div className="flex justify-between items-start gap-3">
                <div className="flex-1">
                  <p className="text-[11px] uppercase tracking-widest text-gold">{b.service_name}</p>
                  <h3 className="text-xl font-serif text-espresso leading-tight mt-0.5">{b.customer_name}</h3>
                </div>
                <span className={`text-[10px] uppercase tracking-widest px-3 py-1 rounded-full ${STATUS_STYLES[b.status] || STATUS_STYLES.pending}`}>
                  {b.status}
                </span>
              </div>

              <div className="mt-3 space-y-1.5 text-sm text-espresso">
                <div className="flex items-center gap-2 text-mocha">
                  <CalendarClock size={14} className="text-gold" />
                  <span>{b.date} at {b.time}</span>
                </div>
                <div className="flex items-center gap-2 text-mocha">
                  <Phone size={14} className="text-gold" />
                  <a href={`tel:${b.customer_phone}`} className="hover:text-espresso">{b.customer_phone}</a>
                </div>
                <div className="flex items-center gap-2 text-mocha">
                  <Mail size={14} className="text-gold" />
                  <a href={`mailto:${b.customer_email}`} className="hover:text-espresso">{b.customer_email}</a>
                </div>
                {b.notes && (
                  <div className="flex items-start gap-2 text-mocha">
                    <MessageSquare size={14} className="text-gold mt-0.5" />
                    <span className="italic">"{b.notes}"</span>
                  </div>
                )}
                <div className="text-mocha text-xs pt-1">${b.service_price?.toFixed(2)} · {b.staff_name || "Simmi Chahal"}</div>
              </div>

              <div className="mt-4 flex gap-2 flex-wrap">
                {b.status !== "confirmed" && (
                  <button onClick={() => changeStatus(b.id, "confirmed")} data-testid={`confirm-${b.id}`}
                    className="text-xs px-3 py-1.5 rounded-full bg-blush/60 text-espresso hover:bg-blush transition">
                    Confirm
                  </button>
                )}
                {b.status !== "completed" && (
                  <button onClick={() => changeStatus(b.id, "completed")} data-testid={`complete-${b.id}`}
                    className="text-xs px-3 py-1.5 rounded-full bg-creamDeep text-espresso hover:bg-borderSoft transition flex items-center gap-1">
                    <CheckCheck size={12} /> Complete
                  </button>
                )}
                {b.status !== "cancelled" && (
                  <button onClick={() => changeStatus(b.id, "cancelled")} data-testid={`cancel-${b.id}`}
                    className="text-xs px-3 py-1.5 rounded-full bg-red-50 text-red-600 hover:bg-red-100 transition flex items-center gap-1">
                    <X size={12} /> Cancel
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Admin;
